// G-MultiFormat — run several formats on ONE event at once (Golf Genius's
// "multiple games per round"). Pure COMPOSITION: no new scoring — it dispatches
// to the already-built, already-tested engines and returns one panel per active
// game. No React, no Firebase, no DB — unit-testable under `node --test`.
import { stablefordStandings, computeSkins, scrambleStandings } from './ggFormats.js'
import { buildReports } from './ggReports.js'

// Fixed order: Leaderboard → Stableford → Skins → Scramble → Flight Winners →
// Match Results → Calcutta. Gross/net are separate rows so the picker is a flat
// checklist and the "10+" count is honest.
export const FORMAT_REGISTRY = [
  { key: 'leaderboard-gross', label: '4-Ball Best Ball — Gross', kind: 'standings', family: 'leaderboard', net: false },
  { key: 'leaderboard-net',   label: '4-Ball Best Ball — Net',   kind: 'standings', family: 'leaderboard', net: true },
  { key: 'stableford-gross',  label: 'Stableford — Gross',       kind: 'standings', family: 'stableford',  net: false },
  { key: 'stableford-net',    label: 'Stableford — Net',         kind: 'standings', family: 'stableford',  net: true },
  { key: 'skins-gross',       label: 'Skins — Gross',            kind: 'skins',     family: 'skins',       net: false },
  { key: 'skins-net',         label: 'Skins — Net',              kind: 'skins',     family: 'skins',       net: true },
  { key: 'scramble-gross',    label: 'Scramble — Gross',         kind: 'standings', family: 'scramble',    net: false },
  { key: 'scramble-net',      label: 'Scramble — Net',           kind: 'standings', family: 'scramble',    net: true },
  { key: 'flight-winners',    label: 'Flight Winners',           kind: 'winners',   family: 'reports' },
  { key: 'match-results',     label: 'Match Results',            kind: 'matches',   family: 'reports' },
  { key: 'calcutta',          label: 'Calcutta Payouts',         kind: 'payouts',   family: 'reports' },
]

// Shown on open — a moderate set so the multi-format value is immediate.
export const DEFAULT_SELECTION = ['leaderboard-gross', 'leaderboard-net', 'skins-gross', 'stableford-gross']

const NEEDS_REPORTS = new Set(['leaderboard', 'reports'])

// Returns active games in FIXED REGISTRY ORDER (not selection order). Unknown
// keys are ignored. buildReports is computed ONCE and shared.
export function runFormats(sample, selectedKeys, options = {}) {
  const selected = new Set(selectedKeys || [])
  const active = FORMAT_REGISTRY.filter((r) => selected.has(r.key))
  if (active.length === 0) return []
  const reports = active.some((r) => NEEDS_REPORTS.has(r.family)) ? buildReports(sample) : null
  const sfCfg = options.stableford || { mode: 'standard' }
  const skinsOpts = options.skins || {}
  return active.map((r) => {
    let result = null
    switch (r.family) {
      case 'leaderboard': result = r.net ? reports.overall.net : reports.overall.gross; break
      case 'stableford':  result = stablefordStandings(sample, { net: r.net, cfg: sfCfg }); break
      case 'scramble':    result = scrambleStandings(sample, { net: r.net }); break
      case 'skins':       result = computeSkins(sample, { net: r.net, ...skinsOpts }); break
      case 'reports':
        if (r.kind === 'payouts') result = reports.calcutta
        else result = reports.flights // winners + matches both read the flights array
        break
      default: result = null
    }
    return { key: r.key, label: r.label, kind: r.kind, net: r.net ?? null, result }
  })
}

// ── Combined CSV — one "# {label}" section per active game ──────────────────
const csv = (s) => (s == null ? '' : `"${String(s).replace(/"/g, '""')}"`)

function sectionFor(panel) {
  const lines = [`# ${panel.label}`]
  const r = panel.result
  if (panel.kind === 'standings') {
    if (panel.key.startsWith('stableford')) {
      lines.push('Place,Team,Points')
      r.forEach((x) => lines.push([x.place, csv(x.teamName), x.points].join(',')))
    } else if (panel.key.startsWith('scramble')) {
      lines.push('Place,Team,Team Score,Vs Par,Net')
      r.forEach((x) => lines.push([x.place, csv(x.teamName), x.teamScore, x.vsPar, x.netScore].join(',')))
    } else {
      lines.push('Place,Team,Gross,Vs Par')
      r.forEach((x) => lines.push([x.place, csv(x.teamName), x.gross ?? '', x.vsPar ?? ''].join(',')))
    }
  } else if (panel.kind === 'skins') {
    lines.push('Team,Skins,Value')
    r.teams.forEach((t) => lines.push([csv(t.teamName), t.skins, t.value].join(',')))
  } else if (panel.kind === 'winners') {
    lines.push('Flight,1st,2nd,3rd')
    r.forEach((f) => lines.push([csv(f.flightName), ...f.winners.gross.slice(0, 3).map((w) => csv(w.teamName))].join(',')))
  } else if (panel.kind === 'matches') {
    lines.push('Flight,Match,Result,Winner')
    r.forEach((f) => f.matches.forEach((m) => lines.push([csv(f.flightName), csv(`${m.teamAName} vs ${m.teamBName}`), csv(m.result), csv(m.winnerName)].join(','))))
  } else if (panel.kind === 'payouts') {
    lines.push('Place,Pct,Amount,Team,Owner')
    r.rows.forEach((x) => lines.push([x.place, x.pct, x.amount, csv(x.teamName), csv(x.owner)].join(',')))
  }
  return lines.join('\n')
}

export function combinedCsv(panels) {
  return (panels || []).map(sectionFor).join('\n\n')
}
